import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Button, Avatar, Dropdown, Space, Typography, Badge, Alert, Tooltip, Drawer } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  DashboardOutlined,
  TeamOutlined,
  CustomerServiceOutlined,
  SettingOutlined,
  BellOutlined,
  MonitorOutlined,
  FileTextOutlined,
  DollarOutlined,
  BarChartOutlined,
  SafetyOutlined,
  DesktopOutlined,
  CloudUploadOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import {
  MENU_ITEMS,
  getRoleBasedTheme,
  getRoleBasedMessages,
  hasPermission
} from '../../utils/rolePermissions';
import websocketService from '../../services/websocket';
import './Layout.css';

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

const Layout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [wsStatus, setWsStatus] = useState({ isConnected: false, isConnecting: false });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Rol bazlı tema ve mesajlar
  const theme = getRoleBasedTheme(user?.role);
  const messages = getRoleBasedMessages(user?.role);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuVisible(false);
  }, [location.pathname]);

  // Görünür menü öğelerini PermissionContext'ten al
  const { menuItems: allowedMenuKeys, permissionsLoaded } = usePermissions();

  const visibleMenuItems = permissionsLoaded
    ? Object.values(MENU_ITEMS).filter(item => allowedMenuKeys.includes(item.key))
    : [MENU_ITEMS.dashboard]; // Yüklenene kadar sadece dashboard

  const iconMap = {
    DashboardOutlined: <DashboardOutlined />,
    TeamOutlined: <TeamOutlined />,
    CustomerServiceOutlined: <CustomerServiceOutlined />,
    MonitorOutlined: <MonitorOutlined />,
    FileTextOutlined: <FileTextOutlined />,
    DollarOutlined: <DollarOutlined />,
    BarChartOutlined: <BarChartOutlined />,
    BellOutlined: <BellOutlined />,
    SettingOutlined: <SettingOutlined />,
    SafetyOutlined: <SafetyOutlined />,
    DesktopOutlined: <DesktopOutlined />,
    CloudUploadOutlined: <CloudUploadOutlined />,
    UserOutlined: <UserOutlined />,
    ShopOutlined: <ShopOutlined />,
  };

  const handleMenuClick = (key) => {
    navigate(key);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profil',
      onClick: () => navigate('/profile')
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Çıkış Yap',
      onClick: handleLogout
    }
  ];

  // Hoş geldin mesajını 5 saniye sonra gizle
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWelcome(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  // WebSocket durumunu takip et (event-based)
  useEffect(() => {
    // İlk durumu al
    setWsStatus(websocketService.getConnectionStatus());

    // Status değişikliklerini dinle (event-based - polling yerine)
    const unsubscribe = websocketService.onStatusChange((status) => {
      setWsStatus(status);
    });

    // Cleanup
    return () => {
      unsubscribe();
    };
  }, []);

  // Rol bazlı stil uygula
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', theme.primaryColor);
    root.style.setProperty('--background-color', theme.backgroundColor);
    root.style.setProperty('--header-color', theme.headerColor);
  }, [theme]);

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      {!isMobile && (
        <Sider 
          trigger={null} 
          collapsible 
          collapsed={collapsed}
          breakpoint="lg"
          collapsedWidth={80}
          style={{
            background: theme.headerColor,
            boxShadow: '2px 0 8px rgba(0,0,0,0.15)'
          }}
        >
        <div className="logo" style={{ 
          height: 64, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'white',
          fontSize: collapsed ? '16px' : '20px',
          fontWeight: 'bold',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          {collapsed ? 'F' : 'Flexer'}
        </div>
        
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          onClick={({ key }) => handleMenuClick(key)}
          style={{ 
            background: theme.headerColor,
            borderRight: 'none'
          }}
          items={visibleMenuItems.map((item) => {
            const IconComponent = iconMap[item.icon] || <SettingOutlined />; // Fallback icon
            return {
              key: item.key,
              icon: IconComponent,
              label: item.label,
              style: {
                margin: '4px 8px',
                borderRadius: '6px',
                height: '40px',
                lineHeight: '40px'
              }
            };
          })}
        />
        </Sider>
      )}

      <AntLayout>
        <Header 
          style={{ 
            padding: '0 16px', 
            background: theme.headerColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          <Space>
            <Button
              type="text"
              icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
              onClick={() => {
                if (isMobile) {
                  setMobileMenuVisible(!mobileMenuVisible);
                } else {
                  setCollapsed(!collapsed);
                }
              }}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64,
                color: 'white'
              }}
            />
            
            <div style={{ color: 'white' }}>
              <Text strong style={{ color: 'white', fontSize: '16px' }}>
                {user?.username} - {user?.role?.display_name || user?.role?.name || user?.role}
              </Text>
            </div>
          </Space>

          <Space>
            {/* WebSocket durumu */}
            <Tooltip title={`WebSocket: ${wsStatus.isConnected ? 'Bağlı' : wsStatus.isConnecting ? 'Bağlanıyor...' : 'Bağlantı Yok'}`}>
              <Badge 
                status={wsStatus.isConnected ? 'success' : wsStatus.isConnecting ? 'processing' : 'error'} 
                text={wsStatus.isConnected ? 'WS' : 'WS'}
                style={{ color: 'white' }}
              />
            </Tooltip>

            {/* Kullanıcı menüsü */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
              popupRender={(menu) => (
                <div style={{ 
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.12)'
                }}>
                  {menu}
                </div>
              )}
            >
              <Space style={{ cursor: 'pointer', color: 'white' }}>
                <Avatar 
                  icon={<UserOutlined />} 
                  style={{ 
                    backgroundColor: theme.primaryColor,
                    cursor: 'pointer'
                  }} 
                />
                <Text style={{ color: 'white' }}>
                  {user?.username}
                </Text>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            margin: '16px',
            padding: '16px',
            background: theme.backgroundColor,
            borderRadius: '8px',
            minHeight: 'calc(100vh - 112px)'
          }}
        >
          {/* Hoş geldin mesajı */}
          {showWelcome && (
            <Alert
              message="Hoş Geldiniz!"
              description={messages.welcome}
              type="info"
              showIcon
              closable
              onClose={() => setShowWelcome(false)}
              style={{ marginBottom: '16px' }}
            />
          )}



          <Outlet />
        </Content>
      </AntLayout>

      {/* Mobile Drawer */}
      <Drawer
        title={
          <Space>
            <DesktopOutlined />
            <span>Flexer Server</span>
          </Space>
        }
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={280}
        styles={{
          body: { padding: 0 },
          header: { 
            background: theme.headerColor,
            color: 'white',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }
        }}
      >
        <div style={{ 
          background: theme.siderBackground,
          height: '100%',
          padding: '16px 0'
        }}>
          <div style={{ 
            padding: '0 16px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            marginBottom: '16px'
          }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong style={{ color: 'white', fontSize: '16px' }}>
                {user?.username}
              </Text>
              <Text type="secondary" style={{ color: 'rgba(255,255,255,0.7)' }}>
                {user?.role?.display_name || user?.role?.name || user?.role}
              </Text>
            </Space>
          </div>
          
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            style={{ 
              background: 'transparent',
              border: 'none',
              color: 'white'
            }}
            items={visibleMenuItems.map(item => ({
              key: item.key,
              icon: iconMap[item.icon],
              label: item.label,
              onClick: () => {
                navigate(item.key);
                setMobileMenuVisible(false);
              }
            }))}
          />
        </div>
      </Drawer>
    </AntLayout>
  );
};

export default Layout;
